import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  FaSync,
  FaBell,
  FaUser,
  FaTh,
  FaBars,
  FaWhatsapp,
} from "react-icons/fa";
import { businessPath } from "../../config/businessMenu";
import ConnectWhatsapp from "../ConnectWhatsapp";
import {
  fetchAllnotification,
  fetchProjectsById,
} from "../../api/customer.api";
import { syncWabaAccount } from "../../api/dashboard.api";
import { invalidateProjectPhoneCache } from "../../hooks/useProjectPhone";
import { useDashboardGate } from "./DashboardGateContext";
import { canAccessAllProjects, getDisplayName } from "../../utils/auth";
import { countUnreadNotifications, markNotificationsSeen } from "../../utils/notifications";
import ThemeSwitch from "../ThemeSwitch";

const formatNotifiedAt = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const BusinessTopBar = ({
  onMenuToggle,
  sidebarCollapsed,
  onExpandSidebar,
}) => {
  const { projectId } = useParams();
  const location = useLocation();
  const isDashboard = /(^|\/)dashboard\/?$/.test(location.pathname);
  const adminName = getDisplayName() || "Admin";
  const { planExpired } = useDashboardGate();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifyLoading, setNotifyLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [waConnected, setWaConnected] = useState(null);
  const [waNumberId, setWaNumberId] = useState(null);
  const notifyRef = useRef(null);
  const connectWhatsappRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetchAllnotification();
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data || [];
        const next = Array.isArray(list) ? list : [];
        setNotifications(next);
        setUnreadCount(countUnreadNotifications(next));
      } catch {
        if (!cancelled) setNotifications([]);
      } finally {
        if (!cancelled) setNotifyLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!notifyOpen) return undefined;

    const onPointerDown = (event) => {
      if (notifyRef.current && !notifyRef.current.contains(event.target)) {
        setNotifyOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setNotifyOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [notifyOpen]);

  useEffect(() => {
    if (!notifyOpen || notifications.length === 0) return;
    markNotificationsSeen(notifications);
    setUnreadCount(0);
  }, [notifyOpen, notifications]);

  const fetchWaStatus = useCallback(async () => {
    if (!projectId) return null;
    const res = await fetchProjectsById({ id: projectId });
    const project = res?.project || res?.data || res;
    const numberId = project?.phone_number_id
      ? String(project.phone_number_id).trim()
      : null;

    setWaNumberId(numberId || null);
    setWaConnected(Boolean(numberId));
    return numberId;
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await fetchWaStatus();
      } catch {
        if (!cancelled) {
          setWaConnected(false);
          setWaNumberId(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchWaStatus]);

  useEffect(() => {
    if (!isDashboard) return;
    if (planExpired === true) {
      setStatusModalOpen(false);
      return;
    }
    if (planExpired !== false || waConnected !== false) return;
    setStatusModalOpen(true);
  }, [isDashboard, planExpired, waConnected, projectId]);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      if (projectId) {
        const syncRes = await syncWabaAccount(projectId);
        invalidateProjectPhoneCache(projectId);
        if (syncRes?.cleared || syncRes?.connected === false) {
          setWaConnected(false);
          setWaNumberId(null);
        }
      }
      await fetchWaStatus();
    } catch {
      setWaConnected(false);
      setWaNumberId(null);
    } finally {
      setSyncing(false);
    }
  };

  const closeStatusModal = () => setStatusModalOpen(false);

  const toggleNotifications = () => {
    setNotifyOpen((open) => !open);
  };

  const handleReconnect = () => {
    window.open(
      "https://business.facebook.com/wa/manage/",
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <>
    <header className="biz-topbar">
      <div className="biz-topbar-left">
        <button
          type="button"
          className="biz-menu-btn u-hide-desktop"
          onClick={onMenuToggle}
          aria-label="Open navigation menu"
        >
          <FaBars />
        </button>
        {sidebarCollapsed && (
          <button
            type="button"
            className="biz-menu-btn biz-sidebar-expand-btn"
            onClick={onExpandSidebar}
            aria-label="Expand navigation menu"
            title="Expand menu"
          >
            <FaBars />
          </button>
        )}
        {canAccessAllProjects() && (
          <Link to="/all-projects" className="biz-all-projects-btn">
            <FaTh />
            <span>Projects</span>
          </Link>
        )}
      </div>

      <div className="biz-topbar-right">
        <button
          type="button"
          className={`biz-sync-btn${syncing ? " is-syncing" : ""}`}
          onClick={handleSync}
          disabled={syncing}
        >
          <FaSync />
          <span>{syncing ? "Syncing…" : "Sync"}</span>
        </button>
        <button
          type="button"
          className={`biz-connected-pill${
            waConnected === false ? " biz-connected-pill--disconnected" : ""
          }`}
          onClick={() => setStatusModalOpen(true)}
          title={
            waConnected
              ? `WhatsApp number linked (${waNumberId})`
              : "No WhatsApp Business number linked to this project"
          }
        >
          <span className="biz-connected-dot" aria-hidden />
          {waConnected === false ? "Not Connected" : "Connected"}
        </button>
        <span className="biz-topbar-divider" aria-hidden />
        <ThemeSwitch />
        <span className="biz-topbar-divider" aria-hidden />
        <Link
          to={businessPath(projectId, "myprofile")}
          className="biz-topbar-admin"
          title="Profile"
        >
          <span className="biz-topbar-avatar" aria-hidden>
            <FaUser />
          </span>
          <span className="biz-topbar-admin-name">{adminName}</span>
        </Link>
        <div className="biz-notify-wrap" ref={notifyRef}>
          <button
            type="button"
            className="biz-topbar-link biz-notify-btn"
            onClick={toggleNotifications}
            aria-haspopup="true"
            aria-expanded={notifyOpen}
            aria-label={`Notifications (${unreadCount})`}
          >
            <FaBell />
            {unreadCount > 0 && (
              <span className="biz-notify-badge">{unreadCount}</span>
            )}
          </button>

          {notifyOpen && (
            <div className="biz-notify-panel" role="dialog" aria-label="Notifications">
              <div className="biz-notify-panel-head">
                <strong>Notifications</strong>
                <button
                  type="button"
                  className="biz-notify-panel-close"
                  onClick={() => setNotifyOpen(false)}
                  aria-label="Close notifications"
                >
                  ×
                </button>
              </div>

              <div className="biz-notify-panel-body">
                {notifyLoading ? (
                  <p className="biz-notify-empty">Loading…</p>
                ) : notifications.length === 0 ? (
                  <p className="biz-notify-empty">No notifications yet.</p>
                ) : (
                  <ul className="biz-notify-list">
                    {notifications.map((item, index) => (
                      <li key={item._id || index} className="biz-notify-item">
                        <p className="biz-notify-item-title">
                          {item.title || "Notification"}
                        </p>
                        {item.description && (
                          <p className="biz-notify-item-text">{item.description}</p>
                        )}
                        <span className="biz-notify-item-time">
                          {formatNotifiedAt(item.createdAt)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>

    {statusModalOpen && (
      <div
        className="biz-wa-status-backdrop"
        onClick={closeStatusModal}
        role="presentation"
      >
        <div
          className={`biz-wa-status-modal ${
            waConnected === false
              ? "biz-wa-status-modal--disconnected"
              : "biz-wa-status-modal--connected"
          }`}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="biz-wa-status-title"
        >
          <button
            type="button"
            className="biz-wa-status-close"
            onClick={closeStatusModal}
            aria-label="Close"
          >
            ×
          </button>

          <h2 id="biz-wa-status-title" className="biz-wa-status-title">
            <FaWhatsapp aria-hidden />
            {waConnected === false
              ? "WhatsApp Not Connected"
              : "WhatsApp Connected"}
          </h2>

          <p className="biz-wa-status-message">
            {waConnected === false
              ? "This project is not linked to a WhatsApp Business number yet. Connect a number to start sending campaigns and receiving live chats."
              : "Your WhatsApp Business API is active and running smoothly. You can send and receive messages without interruption."}
          </p>

          <div className="biz-wa-status-actions">
            {waConnected === false ? (
              <button
                type="button"
                className="biz-wa-status-reconnect"
                onClick={() => {
                  closeStatusModal();
                  connectWhatsappRef.current?.launch();
                }}
              >
                <FaWhatsapp aria-hidden />
                <span>
                  Connect
                  <br />
                  WhatsApp
                </span>
              </button>
            ) : (
              <button
                type="button"
                className="biz-wa-status-reconnect"
                onClick={handleReconnect}
              >
                <FaWhatsapp aria-hidden />
                <span>
                  Reconnect
                  <br />
                  WhatsApp
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    )}

    <ConnectWhatsapp
      ref={connectWhatsappRef}
      projectId={projectId || null}
      hideTrigger
      autoLaunch
      onConnected={fetchWaStatus}
    />
    </>
  );
};

export default BusinessTopBar;
